import { useCallback, useEffect, useState } from 'react';

import {
  error as commonError,
  utils as commonUtils,
} from '@finley-technologies/common';
import { DataPipeline } from '@finley-technologies/finley-types';

import { useUploadContextArtifactInstance } from 'packages/dataPipeline/hooks/UploadContext';

export type ServerState = DataPipeline.Instance['status'];

export const clientUploadStateIterable = [
  'client_initial',
  'client_started',
  'client_uploaded',
  'client_error',
] as const;

export const serverUploadStateIterable: readonly ServerState[] = [
  'initializing',
  'inProgress',
  'success',
  'error',
] as const;

export const UploadStateIterable = [
  ...clientUploadStateIterable,
  ...serverUploadStateIterable,
] as const;

// uploads go thru various states. Following is list (in order) of states
export type UploadState = typeof UploadStateIterable[number];

export const useInputNode = ({ artifactId }: { artifactId: string }) => {
  const [instanceState, setInstanceState] = useState<UploadState>(
    'client_initial',
  );

  const [progress, setProgress] = useState<number>(0);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // once we have a server state, we should update the instance state.
  const {
    instance,
    sanitized,
    directUpload,
  } = useUploadContextArtifactInstance(artifactId);
  const instanceStateFromServer = instance?.status ?? null;
  useEffect(() => {
    if (instanceStateFromServer) {
      setInstanceState(instanceStateFromServer);
    }
  }, [instanceStateFromServer]);

  const upload = useCallback(
    async (file: File) => {
      setInstanceState('client_started');
      try {
        await directUpload({
          file,
          onEventCallback: ({ loaded, total }) =>
            setProgress(commonUtils.divideByOrZero(total, loaded) * 100),
        });
        setInstanceState('client_uploaded');
        setProgress(0);
      } catch (e) {
        setErrorMessage(`upload failed:${commonError.getErrorMessage(e)}`);
        setInstanceState('client_error');
      }
    },
    [directUpload],
  );

  const cashUploadStarted = () => {
    setInstanceState('client_started');
  };
  const cashUploadEnded = () => {
    setInstanceState('client_uploaded');
  };

  // if the instance state is client_initial, upload should be enabled.
  const uploadNeeded = instanceState === 'client_initial';

  return {
    upload,
    instanceState,
    sanitized,
    errorMessage,
    progress,
    cashUploadStarted,
    cashUploadEnded,
    uploadNeeded,
  };
};
